
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Actions from '../../../components/docs/Actions';
import Rocket from 'plaid-threads/Icons/Rocket';

export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'guide',
  section: 'Transfer',
  title: 'Transfer Application',
  description: 'Get approved to create transfers'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Transfer Application' subtitle={metadata.description} mdxType="Header" />
    <Actions.SmallWrapper>
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Request Access' link='https://dashboard.plaid.com/overview/production' />
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Create Transfers' link='/docs/transfer/creating-transfers' />
    </Actions.SmallWrapper>
    <p>{`When you `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/overview/request-products"
      }}>{`request access to Transfer in Production`}</a>{`, Plaid will collect information from you regarding your business and customer verification processes. If your company is privately held, you will also need to submit identity verification details for a "control person" (such as an executive or senior manager).`}</p>
    <h4 {...{
      "id": "originators-vs-platforms"
    }}>{`Originators vs Platforms`}</h4>
    <p>{`As part of your application, Plaid will confirm what type of Transfer customer you are - either an Originator or a Platform.`}</p>
    <Image src='/assets/img/docs/transfer/originator_platform.png' alt="Image showing two types of transfer customers. Originators move money directly to customers. Platforms involve an intermediary business." breakout={true} caption='Types of Transfer customers' mdxType="Image" />
    <Callout mdxType="Callout">
      <p>{`Transfer for Platforms is still in beta. If you are interested in Transfer for Platforms, `}<a parentName="p" {...{
          "href": "https://plaid.com/contact/"
        }}>{`contact sales`}</a>{` or your Plaid account manager, or see the `}<a parentName="p" {...{
          "href": "/docs/transfer/platform-payments"
        }}>{`documentation`}</a>{` to learn more.`}</p>
    </Callout>
    <h5 {...{
      "id": "what-is-an-originator"
    }}>{`What is an Originator?`}</h5>
    <p>{`An Originator initiates ACH payments and is the main beneficiary of these funds. Originators are parties to every transfer and have a direct contractual relationship with the consumer. If you are not contractually obligated to be the main beneficiary of the funds, you are likely a `}<a parentName="p" {...{
        "href": "#what-is-a-platform"
      }}>{`Platform`}</a>{`.`}</p>
    <h6 {...{
      "id": "examples-of-originators"
    }}>{`Examples of Originators:`}</h6>
    <ul>
      <li parentName="ul">{`Neobank funding a new user account`}</li>
      <li parentName="ul">{`E-commerce website offering ACH as a payment method`}</li>
      <li parentName="ul">{`Lender disbursing loan funds to a user`}</li>
    </ul>
    <h5 {...{
      "id": "what-is-a-platform"
    }}>{`What is a Platform?`}</h5>
    <p>{`A Platform onboards originators of their own and facilitates money movement between them and their consumers. Platforms do not have a direct contractual relationship with their originators' consumers and are not obligated to be paid; they are simply an intermediary.`}</p>
    <h6 {...{
      "id": "examples-of-platforms"
    }}>{`Examples of Platforms:`}</h6>
    <ul>
      <li parentName="ul">{`Marketplace website that brings buyers and sellers together`}</li>
      <li parentName="ul">{`Any reseller partner, such as a payment processor that provides an SDK to their customers using Plaid transfer functionality`}</li>
      <li parentName="ul">{`SaaS provider that sells account funding tools or payment services`}</li>
      <li parentName="ul">{`Crowdfunding website for nonprofit organizations`}</li>
    </ul>
    <p>{`To learn more about Plaid's Platforms solution, see the `}<a parentName="p" {...{
        "href": "/docs/transfer/platform-payments"
      }}>{`Transfer for Platforms documentation`}</a>{`.`}</p>
    <h4 {...{
      "id": "prohibited-industries"
    }}>{`Prohibited industries`}</h4>
    <p>{`There are certain types of businesses, products and industries that can't use Plaid's Transfer services. You must not use Plaid's Transfer services for any illegal activities or for the businesses, product types, or industries listed below. The types of industries listed here are representative of prohibited categories, but this is not an exhaustive list.`}</p>
    <ul>
      <li parentName="ul">{`Adult content or services`}</li>
      <li parentName="ul">{`Marijuana dispensaries and related products`}</li>
      <li parentName="ul">{`Payday lending`}</li>
      <li parentName="ul">{`Weapons or munitions`}</li>
    </ul>
    <p>{`To use Plaid's Transfer services, you must remain compliant with these industry limitations. There are additional restricted industries that are subject to enhanced due diligence for approval for Transfer usage.`}</p>
    <p>{`If you have questions about prohibited or restricted industries for Transfer, please `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`contact us`}</a>{`.`}</p>
    <h4 {...{
      "id": "application-process"
    }}>{`Application process`}</h4>
    <p>{`Requesting access to Transfer involves completing the following forms:`}</p>
    <ul>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/onboarding/"
        }}>{`Production Request form`}</a>{` (if you already have Plaid Production access for another product,  request Transfer via the `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/settings/team/products"
        }}>{`Products page`}</a>{` instead)`}</li>
      <li parentName="ul">{`The `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/transfer/questionnaire"
        }}>{`Transfer Application`}</a>{`, also referred to as the Transfer Questionnaire.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://dashboard.plaid.com/transfer/documents"
        }}>{`Transfer Document collection`}</a>{`.`}</li>
    </ul>
    <p>{`The Transfer Application and Transfer Document Collection cannot be started until the Production Request form is complete. However, you can begin these forms while your Production Request is in review.`}</p>
    <p>{`If any of these forms are started but not complete, a link to complete them will appear on the main homepage of your Dashboard. You can also access them directly by clicking the links above.`}</p>
    <p>{`If you don't have all the required information available, you can save any of these forms and complete them later. You can also send links to these forms with other members of your Plaid team to complete. The status of the forms will be shared across the team, so that multiple different team members can each fill out different parts.`}</p>
    <h5 {...{
      "id": "general-information"
    }}>{`General information`}</h5>
    <ul>
      <li parentName="ul">{`Description of business`}</li>
      <li parentName="ul">{`Industry`}</li>
      <li parentName="ul">{`General use case(s) (dropdown selection)`}</li>
      <li parentName="ul">{`Business entity and individual information `}</li>
      <li parentName="ul">{`Requested Plaid products`}</li>
    </ul>
    <h5 {...{
      "id": "transfer-specific-information"
    }}>{`Transfer-specific information`}</h5>
    <ul>
      <li parentName="ul">{`Specific description of Transfer use case`}</li>
      <li parentName="ul">{`ACH processing history`}</li>
      <li parentName="ul">{`ACH processing projections `}</li>
    </ul>
    <p>{`If Plaid is unable to validate individual or entity information through primary methods, we may request supplemental documents. Requests may include documentation to verify:`}</p>
    <ul>
      <li parentName="ul">{`Name (Business entity or individual)`}</li>
      <li parentName="ul">{`Address (Business entity or individual)`}</li>
      <li parentName="ul">{`SSN / EIN`}</li>
      <li parentName="ul">{`DOB`}</li>
    </ul>
    <p>{`Certain restricted industries will require Enhanced Due Diligence (EDD) specific to that industry. Materials requested during EDD may include:`}</p>
    <ul>
      <li parentName="ul">{`Proof of relevant licensing or registration for regulated activities`}</li>
      <li parentName="ul">{`Copies of policies and procedures related to compliance with regulatory requirements`}</li>
      <li parentName="ul">{`Regulatory audit records`}</li>
      <li parentName="ul">{`Consumer complaint policies`}</li>
      <li parentName="ul">{`Financial statements`}</li>
      <li parentName="ul">{`Terms and conditions`}</li>
      <li parentName="ul">{`Responses to industry-specific questions`}</li>
    </ul>
    <p>{`Depending on the industry, these materials may be requested either as part of the Transfer application, or later, during the application review process.`}</p>
    <h4 {...{
      "id": "implementation-checklist"
    }}>{`Implementation checklist`}</h4>
    <p>{`After your Transfer access has been approved, additional operational tasks required for implementation can be found under `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/transfer/implementation-checklist"
      }}>{`"Transfer > Tasks"`}</a>{` in the left-hand menu of the Dashboard.`}</p>
    <p>{`For additional tasks required as part of implementation, see the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/launch-center"
      }}>{`Launch Center`}</a>{`.`}</p>
    <h4 {...{
      "id": "transfer-billing"
    }}>{`Transfer billing`}</h4>
    <p>{`Transfer is typically billed as a fee for each transfer, plus a flat fee for each Transfer Item and a Signal or Balance fee for each ruleset evaluation in `}<inlineCode parentName="p">{`/transfer/authorization/create`}</inlineCode>{`. For details of the transfer pricing model, see the `}<a parentName="p" {...{
        "href": "/docs/account/billing/#transfer-fee-model"
      }}>{`Transfer billing documentation`}</a>{`. Exact transfer pricing will vary depending on the details of your business, payments activity, and Plaid integration. After you have completed the Transfer Application form, Plaid will contact you with more information about the pricing that applies to you.`}</p>
    <p>{`Transfer is available only via a Custom plan with a 12-month minimum contract and is not available via Pay-as-you-go. `}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "originators-vs-platforms",
        level: 4,
        title: "Originators vs Platforms",
      },
{
        id: "prohibited-industries",
        level: 4,
        title: "Prohibited industries",
      },
{
        id: "application-process",
        level: 4,
        title: "Application process",
      },
{
        id: "implementation-checklist",
        level: 4,
        title: "Implementation checklist",
      },
{
        id: "transfer-billing",
        level: 4,
        title: "Transfer billing",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
